/* ============================================================
   Product description "Read more / Read less"
   Safe to load globally
   ============================================================ */

(function () {
  function toggleReadmore(root) {
    // Prevent double-binding (important for section reloads)
    if (root.dataset.readmoreInit === '1') return;
    root.dataset.readmoreInit = '1';

    const btn = root.querySelector('[data-readmore-toggle]');
    const rest = root.querySelector('[data-readmore-rest]');
    const more = root.querySelector('[data-readmore-more]');
    const less = root.querySelector('[data-readmore-less]');

    // If there's nothing to expand, do nothing
    if (!btn || !rest || !more || !less) return;

    btn.addEventListener('click', function () {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      const nextOpen = !isOpen;

      btn.setAttribute('aria-expanded', String(nextOpen));

      // Toggle the rest content
      rest.classList.toggle('hidden', !nextOpen);

      // Toggle labels
      more.classList.toggle('hidden', nextOpen);
      less.classList.toggle('hidden', !nextOpen);
    });
  }

  function init() {
    const blocks = document.querySelectorAll('[data-readmore]');
    if (!blocks.length) return; // nothing to do

    blocks.forEach(toggleReadmore);
  }

  // Initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify section reloads (theme editor, AJAX, quick view, etc.)
  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   Description accordion line-clamp "Read more / Read less"
   ============================================================ */

(function () {
  function initClampReadmore(root) {
    if (root.dataset.readmoreClampInit === '1') return;
    root.dataset.readmoreClampInit = '1';

    const content = root.querySelector('[data-readmore-clamp-content]');
    const btn = root.querySelector('[data-readmore-clamp-toggle]');
    const more = root.querySelector('[data-readmore-more]');
    const less = root.querySelector('[data-readmore-less]');

    if (!content || !btn || !more || !less) return;

    // Show button only if content is actually clamped
    function checkOverflow() {
      if (root.classList.contains('is-expanded')) return;
      if (content.scrollHeight > content.clientHeight + 1) {
        btn.classList.remove('hidden');
      } else {
        btn.classList.add('hidden');
      }
    }

    // Check after accordion opens (content may have 0 height while closed)
    const details = root.closest('details');
    if (details) {
      details.addEventListener('toggle', function () {
        if (details.open) requestAnimationFrame(checkOverflow);
      });
      if (details.open) requestAnimationFrame(checkOverflow);
    } else {
      requestAnimationFrame(checkOverflow);
    }

    btn.addEventListener('click', function () {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';
      const nextOpen = !isOpen;

      btn.setAttribute('aria-expanded', String(nextOpen));
      root.classList.toggle('is-expanded', nextOpen);
      more.classList.toggle('hidden', nextOpen);
      less.classList.toggle('hidden', !nextOpen);
    });
  }

  function init() {
    document.querySelectorAll('[data-readmore-clamp]').forEach(initClampReadmore);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();

/* ============================================================
   Gift wrap per cart item (snippets/gift-wrap-option.liquid,
   snippets/gift-wrap-summary.liquid)
   Wrapped items carry a line item property; the gift wrap
   product quantity always equals the number of wrapped items.
   ============================================================ */

(function () {
  function refreshCart() {
    // The cart page reloads after every change, like the theme's own quantity selector
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  function giftWrapQuantity(cart, variantId) {
    return cart.items
      .filter((item) => String(item.variant_id) === variantId)
      .reduce((total, item) => total + item.quantity, 0);
  }

  async function setGiftWrapQuantity(variantId, quantity) {
    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates: { [variantId]: quantity } })
    });

    if (!response.ok) throw new Error('Failed to update gift wrap quantity');

    return response.json();
  }

  // Checkbox under a cart item. On the product page the element has no line key and
  // the checkbox is a plain line item property field of the product form.
  if (!customElements.get('gift-wrap-option')) {
    customElements.define('gift-wrap-option', class extends HTMLElement {
      connectedCallback() {
        if (!this.dataset.lineKey) return;

        // Opening the drawer moves it to <body>, which runs connectedCallback again,
        // so drop the previous listener on disconnect to toggle only once per click
        this.abortController = new AbortController();
        this.input = this.querySelector('input[type="checkbox"]');
        this.input.addEventListener('change', () => this.toggle(), { signal: this.abortController.signal });
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      async toggle() {
        const { lineKey, variantId, propertyName, propertyValue } = this.dataset;
        const wrap = this.input.checked;

        this.input.disabled = true;
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        try {
          const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
          const line = cart.items.find((item) => item.key === lineKey);

          if (!line) throw new Error('Cart item not found');

          const otherWrappedQuantity = cart.items
            .filter((item) => item !== line && String(item.variant_id) !== variantId && item.properties?.[propertyName] === propertyValue)
            .reduce((total, item) => total + item.quantity, 0);

          // Charge first, so an item is never marked as wrapped when the gift wrap product can't be added.
          // If the property change below fails instead, gift-wrap-sync corrects the charge on the next render.
          await setGiftWrapQuantity(variantId, otherWrappedQuantity + (wrap ? line.quantity : 0));

          // change.js replaces all properties, so keep the ones the item already has. Unwrapping blanks the
          // value rather than deleting it, because change.js ignores an empty properties object.
          const properties = { ...line.properties, [propertyName]: wrap ? propertyValue : '' };

          const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: lineKey, quantity: line.quantity, properties })
          });

          if (!response.ok) throw new Error('Failed to update gift wrap');
        } catch (error) {
          console.error('Error updating gift wrap:', error);
        } finally {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          refreshCart();
        }
      }
    });
  }

  // Rendered by the cart only when the gift wrap quantity is out of step with the wrapped
  // items, e.g. after a wrapped item was added from the product page or its quantity changed.
  if (!customElements.get('gift-wrap-sync')) {
    customElements.define('gift-wrap-sync', class extends HTMLElement {
      async connectedCallback() {
        // Opening the drawer moves it to <body> and runs connectedCallback again
        if (this.hasSynced) return;
        this.hasSynced = true;

        const { variantId } = this.dataset;

        try {
          const cart = await setGiftWrapQuantity(variantId, parseInt(this.dataset.quantity));

          // Only re-render when the cart really changed, so a stuck update can't loop
          if (giftWrapQuantity(cart, variantId) !== parseInt(this.dataset.currentQuantity)) {
            refreshCart();
          }
        } catch (error) {
          console.error('Error syncing gift wrap:', error);
        }
      }
    });
  }
})();
